import { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Paperclip, CircleNotch, CheckCircle, Warning } from "@phosphor-icons/react";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.txt,.csv";
const LABELS = {
  dokumen_klaim: "Dokumen Klaim Lengkap",
  surat_kuasa: "Surat Kuasa Investigasi",
  gabungan: "Dokumen Gabungan (Klaim + Surat Kuasa)",
};

const UploadSlot = ({ category, uploading, done, onPick }) => {
  const ref = useRef(null);
  return (
    <div data-testid={`handover-slot-${category}`}
      className={`border p-3 flex items-center justify-between gap-3 ${done ? "border-emerald-300 bg-emerald-50" : "border-border/60"}`}>
      <div className="flex items-center gap-2 min-w-0">
        {done ? <CheckCircle size={18} className="text-emerald-600 shrink-0" weight="fill" />
          : <Warning size={18} className="text-amber-500 shrink-0" />}
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{LABELS[category]}</p>
          <p className="text-[11px] text-muted-foreground">{done ? "Terlampir" : "Wajib dilampirkan"}</p>
        </div>
      </div>
      <input ref={ref} type="file" className="hidden" accept={ACCEPT}
        data-testid={`handover-input-${category}`} onChange={(e) => onPick(e, category)} />
      <Button type="button" size="sm" variant="outline" disabled={uploading}
        data-testid={`handover-upload-${category}`} onClick={() => ref.current?.click()}
        className="rounded-none h-8 gap-1.5 shrink-0">
        {uploading ? <CircleNotch size={14} className="animate-spin" /> : <Paperclip size={14} />}
        {done ? "Ganti" : "Unggah"}
      </Button>
    </div>
  );
};

export default function ExternalHandoverDocs({ claimId, onReadyChange, onUploaded }) {
  const [mode, setMode] = useState("terpisah");
  const [cats, setCats] = useState(new Set());
  const [uploadingCat, setUploadingCat] = useState(null);

  const load = useCallback(() => {
    api.get(`/claims/${claimId}/documents`).then((r) => {
      const s = new Set(r.data.map((d) => d.category).filter(Boolean));
      setCats(s);
    }).catch((err) => console.error("Gagal memuat dokumen:", err));
  }, [claimId]);

  useEffect(() => { load(); }, [load]);

  const ready = mode === "gabungan" ? cats.has("gabungan") : (cats.has("dokumen_klaim") && cats.has("surat_kuasa"));
  useEffect(() => { onReadyChange?.(ready); }, [ready, onReadyChange]);

  const upload = async (e, category) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Ukuran file maksimal 10 MB"); e.target.value = ""; return; }
    setUploadingCat(category);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    try {
      await api.post(`/claims/${claimId}/documents`, fd);
      toast.success(`${LABELS[category]} terlampir`);
      setCats((prev) => new Set(prev).add(category));
      onUploaded?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingCat(null); e.target.value = ""; }
  };

  return (
    <div data-testid="external-handover-docs" className="border border-amber-300 bg-amber-50/60 p-3 space-y-3">
      <div className="flex items-start gap-2">
        <Paperclip size={16} className="text-amber-700 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-800">
          <b>Lampiran wajib pelimpahan eksternal.</b> Sebelum melimpahkan ke Investigator Eksternal, lampirkan
          dokumen klaim lengkap dan Surat Kuasa Investigasi agar klaim dapat diinvestigasi.
        </p>
      </div>

      <Select value={mode} onValueChange={setMode}>
        <SelectTrigger data-testid="handover-mode-select" className="h-9"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="terpisah">Dua dokumen terpisah (Dokumen Klaim + Surat Kuasa)</SelectItem>
          <SelectItem value="gabungan">Satu dokumen gabungan</SelectItem>
        </SelectContent>
      </Select>

      <div className="space-y-2">
        {mode === "terpisah" ? (
          <>
            <UploadSlot category="dokumen_klaim" uploading={uploadingCat === "dokumen_klaim"} done={cats.has("dokumen_klaim")} onPick={upload} />
            <UploadSlot category="surat_kuasa" uploading={uploadingCat === "surat_kuasa"} done={cats.has("surat_kuasa")} onPick={upload} />
          </>
        ) : (
          <UploadSlot category="gabungan" uploading={uploadingCat === "gabungan"} done={cats.has("gabungan")} onPick={upload} />
        )}
      </div>

      <p data-testid="handover-status" className={`text-xs font-medium ${ready ? "text-emerald-700" : "text-amber-700"}`}>
        {ready ? "Lampiran lengkap — klaim siap dilimpahkan." : "Lampiran belum lengkap."}
      </p>
    </div>
  );
}
